import React, { CSSProperties, JSX, ReactNode, useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { CANVAS_W, CANVAS_H, ELEMENT_FONT_KEY } from 'lib/elementCanvas'
import { useFontGate } from 'lib/fontLoader'

export interface ScaledStageProps {
  mode: 'overlay' | 'preview' | 'pane'
  children: ReactNode
  canvasWidth?: number
  canvasHeight?: number
  elementWidth?: number
  elementHeight?: number
  elementWidthFraction?: number
  showCheckerboard?: boolean
  className?: string
  style?: CSSProperties
  'data-testid'?: string
}

const CHECKERBOARD_BG: CSSProperties = {
  backgroundImage:
    'linear-gradient(45deg, rgba(255,255,255,0.06) 25%, transparent 25%), ' +
    'linear-gradient(-45deg, rgba(255,255,255,0.06) 25%, transparent 25%), ' +
    'linear-gradient(45deg, transparent 75%, rgba(255,255,255,0.06) 75%), ' +
    'linear-gradient(-45deg, transparent 75%, rgba(255,255,255,0.06) 75%)',
  backgroundSize: '16px 16px',
  backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0'
}

const MAX_PREVIEW_SCALE = 2
const STAGE_FONT_CLASS = 'font-sans'

export const ScaledStage = ({
  mode,
  children,
  canvasWidth = CANVAS_W,
  canvasHeight = CANVAS_H,
  elementWidth,
  elementHeight,
  elementWidthFraction,
  showCheckerboard,
  className,
  style,
  'data-testid': testId
}: ScaledStageProps): JSX.Element => {
  const containerRef = useRef<HTMLDivElement>(null)
  const [measuredWidth, setMeasuredWidth] = useState<number>(0)
  const [measuredHeight, setMeasuredHeight] = useState<number>(0)

  const elementFontReady = useFontGate(ELEMENT_FONT_KEY)

  useEffect(() => {
    if (mode === 'overlay') return
    const el = containerRef.current
    if (el == null) return
    const handle = (w: number, h: number): void => {
      setMeasuredWidth(w)
      setMeasuredHeight(h)
    }
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect
        if (rect != null) handle(rect.width, rect.height)
      })
      ro.observe(el)
      const rect = el.getBoundingClientRect()
      handle(rect.width, rect.height)
      return () => ro.disconnect()
    }
    const rect = el.getBoundingClientRect()
    handle(rect.width, rect.height)
  }, [mode])

  if (mode === 'pane') {
    const scale = measuredWidth > 0 && measuredHeight > 0
      ? Math.min(measuredWidth / canvasWidth, measuredHeight / canvasHeight)
      : 0
    return (
      <div
        ref={containerRef}
        data-testid={testId ?? 'scaled-stage-pane'}
        className={classNames(STAGE_FONT_CLASS, className)}
        style={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          ...(showCheckerboard !== false ? CHECKERBOARD_BG : null),
          ...style
        }}
      >
        {scale > 0 && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              width: canvasWidth,
              height: canvasHeight,
              transformOrigin: 'center',
              transform: `translate(-50%, -50%) scale(${scale})`
            }}
          >
            {children}
          </div>
        )}
      </div>
    )
  }

  if (mode === 'overlay') {
    return (
      <div
        data-testid={testId ?? 'scaled-stage-overlay'}
        className={className}
        style={{
          position: 'fixed',
          inset: 0,
          width: '100vw',
          height: '100vh',
          ...style
        }}
      >
        {elementFontReady ? children : null}
      </div>
    )
  }

  const widthFraction = elementWidthFraction ?? 0.85
  const targetElementW = measuredWidth * widthFraction
  const rawScale = elementWidth !== undefined && elementWidth > 0
    ? targetElementW / elementWidth
    : (measuredWidth > 0 ? measuredWidth / CANVAS_W : 0)
  const scale = Math.min(rawScale, MAX_PREVIEW_SCALE)

  const verticalPad = 40
  const containerH = elementHeight !== undefined
    ? Math.round(elementHeight * scale + verticalPad)
    : Math.round(720 * scale + verticalPad)

  return (
    <div
      ref={containerRef}
      data-testid={testId ?? 'scaled-stage-preview'}
      className={classNames(STAGE_FONT_CLASS, className)}
      style={{
        position: 'relative',
        width: '100%',
        height: containerH,
        overflow: 'hidden',
        ...(showCheckerboard !== false ? CHECKERBOARD_BG : null),
        ...style
      }}
    >
      {scale > 0 && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: CANVAS_W,
            height: CANVAS_H,
            transformOrigin: 'center',
            transform: `translate(-50%, -50%) scale(${scale})`
          }}
        >
          {children}
        </div>
      )}
    </div>
  )
}

export const CenteredOnCanvas = ({
  children,
  width,
  height
}: {
  children: ReactNode
  width: number
  height?: number
}): JSX.Element => (
  <div
    style={{
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: `${width}px`,
      height: height !== undefined ? `${height}px` : 'auto'
    }}
  >
    {children}
  </div>
)
